<template>
    <div>
        <v-tour :key="tourName" :name="tourName" :steps="resolvedSteps" :options="mergedTourOptions">
            <template slot-scope="tour">
                <transition name="fade">
                    <v-step v-if="tour.steps[tour.currentStep]" :key="tour.currentStep"
                        :step="tour.steps[tour.currentStep]" :previous-step="tour.previousStep"
                        :next-step="tour.nextStep" :stop="handleStop" :skip="tour.skip" :finish="handleFinish"
                        :is-first="tour.isFirst" :is-last="tour.isLast" :labels="tour.labels"
                        :enabled-buttons="tour.enabledButtons"
                        :highlight="(tour.steps[tour.currentStep].params || {}).highlight"
                        :stop-on-fail="stopOnTargetNotFoundForStep" :debug="tour.debug">

                        <template slot="content">
                            <div class="pt-8">
                                <v-img v-if="tour.steps[tour.currentStep].icon" class="mb-6" height="130"
                                    :src="tour.steps[tour.currentStep].icon" contain></v-img>

                                <div class="font-roboto text-subtitle1 plusIndigoDarken2--text text-left label-text"
                                    v-if="tour.steps[tour.currentStep].label">
                                    {{ tour.steps[tour.currentStep].label }}
                                </div>

                                <div class="font-roboto text-h5 plusGreyDarken4--text  text-left title-text"
                                    v-if="tour.steps[tour.currentStep].title">
                                    {{ tour.steps[tour.currentStep].title }}

                                </div>

                                <div class="font-roboto text-subtitle-2 plusGreyDarken3--text flex text-left mt-3 description-text"
                                    v-if="tour.steps[tour.currentStep].description">
                                    {{ tour.steps[tour.currentStep].description }}
                                </div>
                                <div class="close-button" @click="tour.stop">
                                    <v-icon>mdi-close</v-icon>
                                </div>
                            </div>
                        </template>

                        <div slot="actions" class="d-flex flex-column gap-4">

                            <div class="flex justify-center py-8 items-center">
                                <div class="step-dots" :class="{ 'd-none': tour.steps.length === 1 }">
                                    <span v-for="(step, index) in tour.steps" :key="index"
                                        :class="['dot', { active: index === tour.currentStep }]"></span>
                                </div>
                            </div>

                            <div class="d-flex justify-center justify-md-end">
                                <button @click="tour.previousStep" v-if="!tour.isFirst"
                                    class="px-6 py-3 font-roboto text-subtitle2 rounded plusIndigoDarken2--text mr-2 button-control">{{
                                        $t('message.onboarding_back') }}</button>

                                <button @click="tour.nextStep" v-if="!tour.isLast"
                                    class="px-6 py-3 plusIndigoDarken2 font-roboto-medium text-subtitle2 rounded button-control">
                                    {{ $t('message.onboarding_next') }}
                                </button>

                                <button @click="handleFinish" v-if="tour.isLast"
                                    class="px-6 py-3 plusIndigoDarken2 font-roboto font-weight-medium text-subtitle2 rounded button-control">
                                    {{ finishButtonLabel }}
                                </button>
                            </div>
                        </div>
                    </v-step>
                </transition>
            </template>

        </v-tour>
    </div>
</template>

<script>
import { buildAdminPlusDrawerSteps } from './adminPlusDrawerSteps'

export default {
    name: 'custom-onboarding',
    props: {
        tourName: {
            type: String,
            default: 'custom-onboarding'
        },
        /** Pass an array for a custom tour; omit or pass null to use the default Admin+ drawer steps */
        steps: {
            type: Array,
            default: null
        },
        /** Set to null to skip navigation after finish. Default matches legacy drawer onboarding. */
        finishRoute: {
            default: () => ({ name: 'academysetup' }),
            validator(val) {
                return val === null || (typeof val === 'object' && !Array.isArray(val))
            }
        },
        /** Merged into vue-tour default options (e.g. stopOnTargetNotFound). */
        tourOptions: {
            type: Object,
            default: () => ({})
        },
        /** Override finish button label; when omitted, uses message.onboarding_finish */
        finishButtonText: {
            type: String,
            default: null
        }
    },
    computed: {
        finishButtonLabel() {
            return this.finishButtonText != null
                ? this.finishButtonText
                : this.$t('message.onboarding_finish')
        },
        resolvedSteps() {
            if (this.steps !== null) {
                return this.steps
            }
            return buildAdminPlusDrawerSteps(this)
        },
        mergedTourOptions() {
            return this.tourOptions
        },
        /** Slotted v-step does not inherit VTour defaults; mirror stopOnTargetNotFound (default true). */
        stopOnTargetNotFoundForStep() {
            return Object.prototype.hasOwnProperty.call(this.tourOptions, 'stopOnTargetNotFound')
                ? this.tourOptions.stopOnTargetNotFound
                : true
        }
    },
    methods: {
        currentTour() {
            return this.$tours && this.$tours[this.tourName]
        },
        async start() {
            for (let i = 0; i < 40; i++) {
                const tour = this.currentTour()
                if (tour) {
                    await tour.start()
                    return
                }
                await new Promise((resolve) => setTimeout(resolve, 25))
            }
        },
        stop() {
            const tour = this.currentTour()
            if (tour) {
                tour.stop()
            }
        },
        handleStop() {
            this.stop()
            this.$emit('stop')
        },
        handleFinish() {
            const tour = this.currentTour()
            if (tour) {
                tour.finish()
            }
            this.$emit('finish')
            if (
                this.finishRoute &&
                typeof this.finishRoute === 'object' &&
                this.finishRoute.name &&
                this.$route &&
                this.$route.name !== this.finishRoute.name
            ) {
                this.$router.push(this.finishRoute).catch(() => { })
            }
        },
        clickTargetElement(stepIndex = null) {
            const tour = this.currentTour()
            if (tour && tour.currentStep !== undefined) {
                const currentStep = stepIndex !== null ? tour.steps[stepIndex] : tour.steps[tour.currentStep]
                const targetElement = document.querySelector(currentStep.target)

                if (targetElement) {
                    targetElement.click()
                }
            }
        },
        handleStepChange() {


        }
    },
    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                const tour = this.currentTour()
                if (tour) {
                    tour.$on('step-changed', this.handleStepChange)
                    this.handleStepChange()
                }
            }, 500)
        })
    },
    beforeDestroy() {
        const tour = this.currentTour()
        if (tour) {
            tour.$off('step-changed', this.handleStepChange)
        }
    }
}
</script>

<style scoped>
.close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 24px;
    height: 24px;
    cursor: pointer;
}

.v-step {
    background-color: #fff !important;
}

.v-tour__content {
    background-color: #fff !important;
    padding: 24px;
}

.tour-content {
    padding: 24px;
}

.step-indicator {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
}


.step-dots {
    display: flex;
    gap: 8px;
    padding-top: 20px;
    padding-bottom: 20px;
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 1px solid #5C6BC0;
    background-color: #fff;
    transition: all 0.3s ease;
    cursor: pointer;
}

.dot.active {
    background-color: #5C6BC0;
    width: 20px;
    border-radius: 4px;
}

.dot:hover {
    background-color: #9FA8DA;
}

/* Buttons */
.btn {
    border: none;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-secondary {
    background-color: #e0e0e0;
    color: #333;
    padding: 8px 16px;
}

.btn-secondary:hover {
    background-color: #d0d0d0;
}

@media (max-width: 768px) {
    .button-control {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .label-text {
        text-align: center !important;
    }

    .title-text {
        text-align: center !important;
    }

    .description-text {
        text-align: center !important;
    }
}
</style>
